import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Chip
} from '@mui/material';
import {
  Email as EmailIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Notifications as NotificationsIcon,
  Queue as QueueIcon,
  CloudUpload as CloudUploadIcon,
  Functions as FunctionsIcon,
  ExpandLess,
  ExpandMore,
  Settings as AutomaticIcon
} from '@mui/icons-material';
import intl from 'components/i18n/ReactIntlWrapper';

import { AutomaticStateType, STATE_TYPE_DEFINITIONS } from './types';

const ICON_MAP = {
  EmailIcon: EmailIcon,
  PictureAsPdfIcon: PictureAsPdfIcon,
  NotificationsIcon: NotificationsIcon,
  QueueIcon: QueueIcon,
  CloudUploadIcon: CloudUploadIcon,
  FunctionsIcon: FunctionsIcon,
};

interface AutomaticActionToolboxProps {
  onStateTypeSelect: (stateType: AutomaticStateType) => void;
  onClose?: () => void;
}

const AutomaticActionToolbox: React.FC<AutomaticActionToolboxProps> = ({
  onStateTypeSelect,
  onClose
}) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['communication', 'documents', 'cloud']);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const categories = {
    communication: {
      name: intl.get('automaticActions.category.communication'),
      stateTypes: [
        AutomaticStateType.SEND_EMAIL_SES,
        AutomaticStateType.SEND_EMAIL_SMTP,
        AutomaticStateType.SEND_NOTIFICATION_SNS
      ]
    },
    documents: {
      name: intl.get('automaticActions.category.documents'),
      stateTypes: [
        AutomaticStateType.GENERATE_PDF,
        AutomaticStateType.GENERATE_INVOICE_NUMBER
      ]
    },
    cloud: {
      name: intl.get('automaticActions.category.cloud'),
      stateTypes: [
        AutomaticStateType.DROP_MESSAGE_SQS,
        AutomaticStateType.STORE_FILE_S3,
        AutomaticStateType.CALL_LAMBDA
      ]
    }
  };

  const handleStateTypeClick = (stateType: AutomaticStateType) => {
    onStateTypeSelect(stateType);
  };

  // Make toolbox items draggable
  const handleDragStart = (event: React.DragEvent, stateType: AutomaticStateType) => {
    event.dataTransfer.setData('application/reactflow', stateType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const renderStateType = (stateType: AutomaticStateType) => {
    const definition = STATE_TYPE_DEFINITIONS[stateType];
    const IconComponent = ICON_MAP[definition.icon as keyof typeof ICON_MAP] || AutomaticIcon;
    return (
      <ListItem key={stateType} disablePadding>
        <ListItemButton
          draggable
          onDragStart={(e) => handleDragStart(e, stateType)}
          onClick={() => handleStateTypeClick(stateType)}
          sx={{
            pl: 4,
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <ListItemIcon>
            <IconComponent color="primary" />
          </ListItemIcon>
          <ListItemText
            primary={intl.get(`automaticActions.${stateType}.name`)}
            secondary={intl.get(`automaticActions.${stateType}.description`)}
            secondaryTypographyProps={{
              variant: 'caption',
              color: 'text.secondary'
            }}
          />
          <Chip
            size="small"
            label={intl.get('automaticActions.paramsCount', { count: definition.parameters.length })}
            variant="outlined"
            sx={{ ml: 1 }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box>
      <Box p={2}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <AutomaticIcon color="primary" />
          <Typography variant="h6">
            {intl.get('automaticActions.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          {intl.get('automaticActions.dragInstruction')}
        </Typography>
      </Box>
      <Divider />
      <List>
        {Object.entries(categories).map(([categoryKey, category]) => (
          <React.Fragment key={categoryKey}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => toggleCategory(categoryKey)}>
                <ListItemText 
                  primary={category.name}
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    fontWeight: 600
                  }}
                />
                {expandedCategories.includes(categoryKey) ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={expandedCategories.includes(categoryKey)} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {category.stateTypes.map(renderStateType)}
              </List>
            </Collapse>
            {categoryKey !== 'cloud' && <Divider />}
          </React.Fragment>
        ))}
      </List>
      <Box p={2}>
        <Typography variant="caption" color="text.secondary">
          Each automatic action requires input parameters and produces SUCCESS or FAILURE outcomes.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Paper
      elevation={3}
      sx={{
        width: 320,
        maxHeight: '80vh',
        overflow: 'auto',
        position: 'fixed',
        left: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1000
      }}
    >
      <Box p={2}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <AutomaticIcon color="primary" />
          <Typography variant="h6">
            Automatic Actions
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Drag and drop these state types to create automatic workflow actions with configurable parameters.
        </Typography>
      </Box>

      <Divider />

      <List>
        {Object.entries(categories).map(([categoryKey, category]) => (
          <React.Fragment key={categoryKey}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => toggleCategory(categoryKey)}>
                <ListItemText 
                  primary={category.name}
                  primaryTypographyProps={{
                    variant: 'subtitle2',
                    fontWeight: 600
                  }}
                />
                {expandedCategories.includes(categoryKey) ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            
            <Collapse in={expandedCategories.includes(categoryKey)} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {category.stateTypes.map(renderStateType)}
              </List>
            </Collapse>
            
            {categoryKey !== 'cloud' && <Divider />}
          </React.Fragment>
        ))}
      </List>

      <Box p={2}>
        <Typography variant="caption" color="text.secondary">
          Each automatic action requires input parameters and produces SUCCESS or FAILURE outcomes.
        </Typography>
      </Box>
    </Paper>
  );
};

export default AutomaticActionToolbox;
